/**
 * 复制框、柱状图、语言切换。
 *
 * 背景：控制台要一次性展示新建的 API Key（只出现一次，必须方便复制）、看每日花费、切换界面语言。
 * 设计意图：
 *   - CopyField 用 navigator.clipboard；不可用（非 https、权限被拒）时提示用户手动选中，值本身可全选。
 *   - Bars 是纯 CSS 柱状图，数据量是天数（最多 92 根），不值得引入图表库。
 * 关键约束：Bars 的 value 必须是非负数；全为 0 时每根柱子保留 2px 高度，表示「有这一天但没花费」。
 */
import { IconCheck, IconCopy, IconLanguage } from '@tabler/icons-react'
import { useState, type ReactNode } from 'react'
import { C } from '@ap/i18n/common'
import { LOCALES, useI18n, type Locale } from '@ap/i18n'
import { Button } from './primitives.tsx'
import { useToast } from './toast.tsx'

export function CopyField({ value, label }: { value: string; label?: ReactNode }) {
  const { t } = useI18n()
  const toast = useToast()
  const [done, setDone] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setDone(true)
      toast.ok(t(C.copied))
      window.setTimeout(() => setDone(false), 1500)
    } catch (e) {
      console.warn('[ui] 剪贴板不可用', e)
      toast.error(t(C.copyFailed))
    }
  }
  return (
    <div className="ap-copy">
      {label}
      <span className="ap-copy__value">{value}</span>
      <Button size="sm" onClick={copy} icon={done ? <IconCheck size={14} /> : <IconCopy size={14} />}>
        {t(C.copy)}
      </Button>
    </div>
  )
}

export function Bars({
  data,
  format,
}: {
  data: { label: string; value: number; title?: string }[]
  format: (v: number) => string
}) {
  const max = Math.max(0, ...data.map((d) => d.value))
  const every = Math.max(1, Math.ceil(data.length / 12))
  return (
    <div className="ap-bars">
      {data.map((d, i) => (
        <div key={d.label} className="ap-bars__col" title={`${d.title ?? d.label}: ${format(d.value)}`}>
          <div className="ap-bars__bar" style={{ height: max > 0 ? `${(d.value / max) * 100}%` : '2px' }} />
          <span className="ap-bars__label">{i % every === 0 ? d.label : '\u00a0'}</span>
        </div>
      ))}
    </div>
  )
}

export function LocaleSelect({ onChange }: { onChange?: (l: Locale) => void }) {
  const { locale, setLocale, t } = useI18n()
  return (
    <label className="ap-locale-select" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }} title={t(C.language)}>
      <IconLanguage size={16} color="var(--fg-soft)" style={{ flex: 'none' }} />
      <select
        className="ap-select ap-select-inline"
        value={locale}
        aria-label={t(C.language)}
        onChange={(e) => {
          const l = e.target.value as Locale
          setLocale(l)
          onChange?.(l)
        }}
      >
        {LOCALES.map((l) => (
          <option key={l.id} value={l.id}>
            {l.label}
          </option>
        ))}
      </select>
    </label>
  )
}
